import { ExternalRedirect } from '#app/components/ExternalRedirect/index.js';
import { useIsInternalTraffic } from '#app/hooks/useIsInternalTraffic.js';
import { useLastLocation } from '#app/hooks/useLastLocation.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { getIndependentPortfolioUrl } from '#app/utilities/getIndependentPortfolioUrl.js';
import { useMemo } from 'react';

type PortfolioRedirectProps = {
  readonly displayUsername?: string;
  readonly route: UserProfileRoutes;
};

type UserProfileRoutes =
  | 'user'
  | 'userProjects'
  | 'userRecommendations'
  | 'userServices';

const getRouteHash = (route: UserProfileRoutes): string => {
  switch (route) {
    case 'userProjects':
      return '#projects';
    case 'userRecommendations':
      return '#recommendations';
    case 'userServices':
      return '#services';
    case 'user':
    default:
      return '';
  }
};

/*
  Used to redirect 3rd party profile views to
  the independent portfolio when published
*/
export const PortfolioRedirect = ({
  displayUsername,
  route = 'user',
}: PortfolioRedirectProps) => {
  const isInternalTraffic = useIsInternalTraffic();
  const lastLocation = useLastLocation();

  const [urlSearchParameters] = useURLParameters('*');
  const startInquiry = urlSearchParameters.startInquiry;
  const justSignedUp = urlSearchParameters.justSignedUp;

  const href = useMemo(() => {
    const parameters = new URLSearchParams({
      ...(startInquiry ? { startInquiry: 'true' } : {}),
      ...(justSignedUp ? { justSignedUp: 'true' } : {}),
      ...(isInternalTraffic
        ? {
            isInternalTraffic: 'true',
          }
        : {}),
      ...(lastLocation?.routeKey
        ? {
            utm_medium: 'contra',
            utm_source: lastLocation?.routeKey,
          }
        : {}),
    });

    const portfolioUrl = getIndependentPortfolioUrl({
      username: displayUsername,
    });

    return `${portfolioUrl}${getRouteHash(route)}${
      parameters.toString() ? `?${parameters.toString()}` : ''
    }`;
  }, [
    displayUsername,
    isInternalTraffic,
    justSignedUp,
    lastLocation?.routeKey,
    route,
    startInquiry,
  ]);

  return <ExternalRedirect href={href} />;
};
